import {
	SESSION_EFFORTS,
	SESSION_MODEL_BEFORE_EFFORT,
	SESSION_MODELS,
	type SessionModel,
	type SessionModelSelection,
	type WebSessionModel,
} from "../../cloud/session-model.ts";
import { h, icon } from "../../ui/dom.ts";
import { phrase, presentationLocale, t } from "../../ui/preferences.ts";
import type { CompanyController } from "../company.ts";

const EFFORT_NAMES: Record<(typeof SESSION_EFFORTS)[number], string> = {
	low: phrase("Low"),
	medium: phrase("Medium"),
	high: phrase("High"),
};

/** An effort's name in the interface language; one without a name here (xhigh, max) reads as its id. */
export function effortName(effort: string): string {
	return Object.hasOwn(EFFORT_NAMES, effort) ? t(EFFORT_NAMES[effort as keyof typeof EFFORT_NAMES]) : effort;
}

/** How much faster Fast runs each model, from the provider's model catalog. */
const FAST_SPEEDS: Record<string, number> = { "gpt-6-astra": 2, "gpt-6-sol": 1.5, "gpt-6-luna": 1.5 };

/** The caption under the Fast switch for the conversation's model: "1.5x
 * speed", or "1,5x hastighed" in Danish. */
function fastCaption(modelId: string | undefined): string {
	const factor = modelId && Object.hasOwn(FAST_SPEEDS, modelId) ? FAST_SPEEDS[modelId] : undefined;
	if (!factor) return t("Faster replies");
	const speed = `${new Intl.NumberFormat(presentationLocale() === "da" ? "da-DK" : "en-GB").format(factor)}x`;
	// The catalog lists Astra's Fast tier as using more of the plan.
	return modelId === "gpt-6-astra"
		? t("{speed} speed, uses more of your plan", { speed })
		: t("{speed} speed", { speed });
}

/** Conversation-scoped model, effort and speed; the server pins them when
 * admitting the member's next run in this conversation. In a group chat the one
 * choice is every agent's there: each agent's reply to a post runs in the group's
 * conversation. */
export class ComposerModels {
	readonly button: HTMLButtonElement;
	readonly el: HTMLElement;
	readonly #label = h("small");
	readonly #status = h("p", { class: "composer-model-status", role: "status" });
	readonly #scope = h("p", { class: "composer-model-scope" });
	readonly #refresh: HTMLButtonElement;
	readonly #choices: HTMLButtonElement[];
	readonly #efforts: HTMLButtonElement[];
	readonly #effortGroup: HTMLElement;
	readonly #fast: HTMLButtonElement;
	readonly #fastCaption = h("small");
	readonly #speedGroup: HTMLElement;
	readonly #company: CompanyController;
	readonly #sessionId: string;
	readonly #client: WebSessionModel | undefined;
	readonly #signal: AbortSignal;
	readonly #changed: () => void;
	#observed: SessionModel | undefined;
	#request: AbortController | undefined;
	#pending: SessionModelSelection | undefined;
	#pressed: HTMLButtonElement | undefined;
	#error = "";
	#started = false;
	#locked = true;

	constructor(
		company: CompanyController,
		sessionId: string,
		client: WebSessionModel | undefined,
		signal: AbortSignal,
		open: () => void,
		back: () => void,
		changed: () => void,
	) {
		this.#company = company;
		this.#sessionId = sessionId;
		this.#client = client;
		this.#signal = signal;
		this.#changed = changed;
		this.button = h(
			"button",
			{ type: "button", role: "menuitem", "aria-haspopup": "menu", "aria-expanded": "false", onclick: open },
			icon("brain"),
			h("span", { class: "composer-model-label" }, t("Model"), this.#label),
			icon("chevron"),
		);
		// Each choice saves only what it changes; the cloud keeps the other.
		this.#choices = SESSION_MODELS.map((model) => {
			const choice: HTMLButtonElement = h(
				"button",
				{
					type: "button",
					role: "menuitemradio",
					"aria-checked": "false",
					onclick: () => void this.#save({ modelId: model.id }, choice),
				},
				h("span", null, model.name),
				icon("check"),
			);
			return choice;
		});
		this.#efforts = SESSION_EFFORTS.map((effort) => {
			const choice: HTMLButtonElement = h(
				"button",
				{
					type: "button",
					role: "menuitemradio",
					"aria-checked": "false",
					onclick: () => void this.#save({ reasoningEffort: effort }, choice),
				},
				h("span", null, t(EFFORT_NAMES[effort])),
				icon("check"),
			);
			return choice;
		});
		this.#effortGroup = h(
			"div",
			{ class: "composer-model-group", role: "group", "aria-label": t("Effort"), hidden: true },
			h("p", { class: "composer-model-heading", "aria-hidden": "true" }, t("Effort")),
			...this.#efforts,
		);
		// Fast is on unless this conversation turned it off.
		this.#fast = h(
			"button",
			{
				type: "button",
				role: "menuitemcheckbox",
				"aria-checked": "false",
				class: "composer-model-fast",
				onclick: () =>
					void this.#save(
						{ serviceTier: this.#observed?.serviceTier === "fast" ? "standard" : "fast" },
						this.#fast,
					),
			},
			h("span", { class: "composer-model-label" }, t("Fast"), this.#fastCaption),
			h("span", { class: "composer-switch", "aria-hidden": "true" }),
		);
		this.#speedGroup = h(
			"div",
			{ class: "composer-model-group", role: "group", "aria-label": t("Speed"), hidden: true },
			h("p", { class: "composer-model-heading", "aria-hidden": "true" }, t("Speed")),
			this.#fast,
		);
		this.#refresh = h(
			"button",
			{ type: "button", role: "menuitem", hidden: true, onclick: () => void this.refresh() },
			t("Refresh model"),
		);
		this.el = h(
			"div",
			{ class: "composer-models", role: "menu", "aria-label": t("Model"), hidden: true },
			h("button", { type: "button", role: "menuitem", onclick: back }, icon("back"), t("Model")),
			...this.#choices,
			this.#effortGroup,
			this.#speedGroup,
			this.#scope,
			this.#status,
			this.#refresh,
		);
	}

	get pending(): boolean {
		return this.#pending !== undefined;
	}

	update(available: boolean, locked: boolean, group = false): void {
		if (this.#signal.aborted) return;
		this.#locked = locked;
		const scope = group
			? t("Every agent in this group chat. Applies from your next message.")
			: t("Only this conversation. Applies from your next message.");
		if (this.#scope.textContent !== scope) this.#scope.textContent = scope;
		this.button.hidden = !available || !this.#client;
		const state = this.#company.store.get();
		if (
			!available ||
			state.accessLost ||
			!state.team?.capabilities.read ||
			(!state.team.capabilities.write && this.pending)
		) {
			this.#request?.abort();
			this.#request = undefined;
			this.#observed = undefined;
			this.#pending = undefined;
			this.#started = false;
			this.#error = "";
		}
		this.#render();
		// Avoid re-entering the parent's initial render.
		if (available && !state.accessLost && state.team?.capabilities.read && this.#client && !this.#started) {
			this.#started = true;
			queueMicrotask(() => void this.refresh());
		}
	}

	async refresh(): Promise<void> {
		if (!this.#client || this.#signal.aborted || this.#request || !this.#canRead()) return;
		const request = new AbortController();
		this.#request = request;
		const signal = AbortSignal.any([request.signal, this.#signal]);
		this.#error = "";
		this.#notify();
		try {
			const value = await this.#client.read(this.#sessionId, signal);
			signal.throwIfAborted();
			this.#observed = value;
			this.#pending = undefined;
		} catch {
			if (!signal.aborted) this.#error = t("The model is unavailable. Refresh to try again.");
		} finally {
			if (this.#request === request) {
				this.#request = undefined;
				this.#notify();
				if (!this.el.hidden && document.activeElement === this.el.firstElementChild) this.focus();
			}
		}
	}

	focus(): void {
		this.el.querySelector<HTMLButtonElement>('[aria-checked="true"]:not(:disabled)')?.focus();
		if (!this.el.contains(document.activeElement)) this.el.querySelector<HTMLButtonElement>("button")?.focus();
	}

	#canRead(): boolean {
		const state = this.#company.store.get();
		return (
			!state.accessLost &&
			state.team?.capabilities.read === true &&
			state.sessions.some((item) => item.sessionId === this.#sessionId)
		);
	}

	async #save(selection: SessionModelSelection, pressed: HTMLButtonElement): Promise<void> {
		if (
			!this.#client ||
			!this.#observed ||
			this.#request ||
			this.#error ||
			this.#signal.aborted ||
			this.button.disabled
		)
			return;
		const request = new AbortController();
		this.#request = request;
		const signal = AbortSignal.any([request.signal, this.#signal]);
		this.#pending = selection;
		this.#pressed = pressed;
		this.#notify();
		try {
			const value = await this.#client.set(this.#sessionId, selection, this.#observed.version, signal);
			signal.throwIfAborted();
			this.#observed = value;
			this.#pending = undefined;
		} catch {
			if (!signal.aborted) this.#error = t("The change could not be confirmed. Refresh the model before sending.");
		} finally {
			if (this.#request === request) {
				this.#request = undefined;
				this.#notify();
				// Focus returns to the choice the member pressed once it is usable again.
				if (!this.el.hidden && document.activeElement === this.el.firstElementChild) {
					if (this.#pressed && !this.#pressed.disabled) this.#pressed.focus();
					else this.focus();
				}
				this.#pressed = undefined;
			}
		}
	}

	#notify(): void {
		if (this.#signal.aborted) return;
		this.#render();
		this.#changed();
	}

	#render(): void {
		const focused = this.el.contains(document.activeElement) ? document.activeElement : null;
		const id = this.#observed?.modelId;
		// A group chat with no model chosen runs each agent on its own model; nothing is ticked.
		const name = id
			? (SESSION_MODELS.find((item) => item.id === id)?.name ?? id)
			: this.#observed
				? t("Each agent's own")
				: undefined;
		const effortId = this.#observed?.reasoningEffort;
		const effort = effortId && effortName(effortId);
		const tier = this.#observed?.serviceTier;
		const fast = tier === "fast";
		const summary = name && [name, effort, fast && t("Fast")].filter(Boolean).join(" · ");
		this.button.disabled = this.#locked || !this.#canRead();
		this.#label.textContent = this.pending
			? this.#error
				? t("Change unconfirmed")
				: t("Saving…")
			: (summary ?? (this.#error ? t("Unavailable") : t("Loading…")));
		const described = name
			? effort
				? t("Model: {name}, {effort} effort", { name, effort })
				: t("Model: {name}", { name })
			: t("Model");
		this.button.setAttribute("aria-label", name && fast ? t("{model}, Fast", { model: described }) : described);
		const unavailable = this.button.disabled || !this.#observed || Boolean(this.#request) || Boolean(this.#error);
		// A cloud that does not honour per-conversation effort omits it, and
		// accepts only GPT-6 Astra of the listed models. Hidden choices stay
		// disabled so arrow keys skip them.
		const beforeEffort = this.#observed !== undefined && effortId === undefined;
		for (const [index, choice] of this.#choices.entries()) {
			const model = SESSION_MODELS[index]?.id;
			choice.setAttribute("aria-checked", String(model === id));
			choice.disabled = unavailable || (beforeEffort && model !== SESSION_MODEL_BEFORE_EFFORT);
		}
		this.#effortGroup.hidden = effortId === undefined;
		for (const [index, choice] of this.#efforts.entries()) {
			choice.setAttribute("aria-checked", String(SESSION_EFFORTS[index] === effortId));
			choice.disabled = unavailable || this.#effortGroup.hidden;
		}
		// A cloud that does not run conversations on the Fast tier omits the
		// speed; an unfamiliar tier reads as off and can be switched on.
		this.#speedGroup.hidden = tier === undefined;
		this.#fast.setAttribute("aria-checked", String(fast));
		this.#fast.disabled = unavailable || this.#speedGroup.hidden;
		this.#fastCaption.textContent = fastCaption(id);
		this.#status.textContent =
			this.#error || (this.#request ? (this.pending ? t("Saving model…") : t("Loading model…")) : "");
		this.#refresh.hidden = !this.#error;
		this.#refresh.disabled = this.button.disabled || Boolean(this.#request);
		// Disabling a focused choice blurs it in browsers. Keep Escape and arrow
		// navigation inside the menu while the request is being confirmed.
		if (focused instanceof HTMLButtonElement && (focused.disabled || focused.hidden))
			this.el.querySelector<HTMLButtonElement>("button")?.focus();
	}
}
